import { Attr, HasMany, HasOne, Model } from "spraypaint";

import { instantInsightsPlusPendingStatuses } from "shared/constants";
import { ApplicationRecord } from "shared/services/spraypaint";

import type ActiveStorageAttachment from "./ActiveStorageAttachment";
import type DistributionList from "./DistributionList";
import type EmailTracking from "./EmailTracking";
import type InsightsQuery from "./InsightsQuery";
import type InsightsReportChart from "./InsightsReportChart";
import type InsightsReportGroup from "./InsightsReportGroup";
import type InsightsReportKeyInsight from "./InsightsReportKeyInsight";
import type Organisation from "./Organisation";
import type ReportFile from "./ReportFile";

type ReportType = "qualitative" | "quantitative" | "insights_plus";

@Model()
class InsightsReport extends ApplicationRecord {
  static jsonapiType = "insights_reports";

  @HasMany() activeStorageAttachments: ActiveStorageAttachment[];

  @Attr() advertisementValueCurrency: string;

  @Attr() after: number;

  @Attr() archivedAt: string;

  @Attr() articleRankingSourceIds: number[];

  @Attr() before: number;

  @Attr() billable: boolean;

  @Attr() brandProfileId: number;

  @Attr() bundleBroadcast: boolean;

  @Attr() coverImageUrl: string;

  @Attr() dateInterval: string;

  @Attr({ persist: false }) deliveredRecipients: string[];

  @HasOne() distributionList: DistributionList;

  @Attr() distributionListId: number;

  @HasOne() emailTracking: EmailTracking;

  @Attr({ persist: false }) exportedAt: string;

  @Attr({ persist: false }) filterSets: any;

  @HasOne() group: InsightsReportGroup;

  @Attr() groupId: number;

  @Attr() ignoreMentionExclusions: boolean;

  @Attr() ignoreNoTopicMentions: boolean;

  @Attr() includeAdvertisementValue: boolean;

  @Attr() includeExternalLinks: boolean;

  @Attr() includeSyndication: boolean;

  @Attr() insightsGlobalEntityGroupIds: number[];

  @Attr() insightsPlusMainQueryId: number;

  @HasMany() insightsQueries: InsightsQuery[];

  @HasMany() insightsReportCharts: InsightsReportChart[];

  @HasMany() insightsReportKeyInsights: InsightsReportKeyInsight[];

  @Attr() label: string;

  @Attr() mentionsAggregationCsvUrl: string;

  @Attr() mentionsCsvUrl: string;

  @Attr() multipleTopics: boolean;

  @Attr() notes: string;

  @HasOne() organisation: Organisation;

  @Attr() organisationId: number;

  @Attr({ persist: false }) processingErrors: string;

  @Attr({ persist: false }) queriesStatusCount: string;

  @Attr() rateInfluence: boolean;

  @Attr() rateKeyMessage: boolean;

  @Attr() ratingMode: string;

  @Attr() recipients: string[];

  @Attr({ persist: false }) recipientsList: string[];

  @Attr() refreshQueries: boolean;

  @HasOne() reportFile: ReportFile;

  @HasMany() reportFiles: ReportFile[];

  @Attr() reportType: ReportType;

  @Attr() secondaryCoverImageUrl: string;

  @Attr() secondaryLabel: string;

  @Attr() selectedQueries: number[];

  @Attr() selectedTopics: number[];

  @Attr({ persist: false }) sentAt: string;

  @Attr() socialCsvUrl: string;

  @Attr() status: string;

  @Attr() topNAggregationCsvUrl: string;

  @Attr() topNCsvUrl: string;

  isCompleted() {
    return this.status === "building_completed";
  }

  isHtmlCompleted() {
    return this.status === "html_completed";
  }

  isInstantInsightsPlusReport() {
    return this.reportType === "insights_plus";
  }

  isProcessing() {
    return instantInsightsPlusPendingStatuses.includes(this.status);
  }

  isQualitativeReport() {
    return this.reportType === "qualitative";
  }

  isQuantitativeReport() {
    return this.reportType === "quantitative";
  }
}

export default InsightsReport;
